





















































import { defineComponent, getCurrentInstance, onBeforeUnmount, ref, watch } from "@vue/composition-api";
import useAuthUser from "@/hooks/useAuthUser";
import useCheckVerificationCode from "@/hooks/useEmailVerification";
import { ACTION_TYPES } from "@/store/const";
import emitter from "@/lib/emitter";
import { LoginSignUpModalData } from "@/types";
import { EVENTS, ROLES } from "@/const";
import BaseButton from "@/components/BaseButton";
import BaseConfirm from "@/components/BaseConfirm";
import LoginComponent from "@/components/LoginComponent";
import ModalView from "@/components/ModalView";
import SignUpComponent from "@/components/SignUpComponent";
import SignUpEmailConfirm from "@/components/SignUpEmailConfirm";
import SignUpEmailConfirmCode from "@/components/SignUpEmailConfirmCode";
import { LoginModalStep } from "./const";

export default defineComponent({
  name: "LoginSignUpModal",
  components: {
    BaseButton,
    BaseConfirm,
    LoginComponent,
    SignUpComponent,
    SignUpEmailConfirm,
    SignUpEmailConfirmCode,
    ModalView,
  },
  setup() {
    const { authUser, allowAccess, socialLogin } = useAuthUser();
    const { startPeriodicalConfirmationCheck, stopPeriodicalConfirmationCheck } = useCheckVerificationCode();

    const vm = getCurrentInstance();
    const show = ref(false);
    const step = ref(LoginModalStep.SIGNUP);
    const showConfirm = ref(false);
    const isSwitchAccount = ref(false);
    const loginRole = ref(ROLES.MEMBER);

    const modalData = ref<LoginSignUpModalData>({});
    const showModal = (data?: LoginSignUpModalData) => {
      show.value = true;
      isSwitchAccount.value = data?.isSwitch ?? false;
      if (data && data.showLogin) {
        step.value = LoginModalStep.LOGIN;
      }
      if (data) modalData.value = data;
    };

    const closeModal = async (result = true) => {
      try {
        if (!result) {
          return;
        }
        if (authUser.value && !allowAccess.value) {
          await vm?.$store.dispatch(ACTION_TYPES.LOGOUT);
        }
        show.value = false;
        step.value = LoginModalStep.SIGNUP;
      } finally {
        showConfirm.value = false;
      }
    };
    const onCloseModal = () => {
      if (authUser.value && !allowAccess.value) {
        showConfirm.value = true;
      } else {
        closeModal();
      }
    };

    const isSignUpFlow = ref(step.value === LoginModalStep.SIGNUP);
    const callCallback = async () => {
      if (isSignUpFlow.value && modalData.value.onSignUpSuccess) {
        await modalData.value.onSignUpSuccess();
      }
      if (!isSignUpFlow.value && modalData.value.onLoginSuccess) {
        await modalData.value.onLoginSuccess();
      }
    };

    const onLogin = async () => {
      if (!allowAccess.value && socialLogin.value) {
        step.value = LoginModalStep.EMAIL_CONFIRM;
      } else if (!allowAccess.value) {
        step.value = LoginModalStep.CODE_CONFIRM;
      } else {
        await callCallback();
        await closeModal();
        if (isSwitchAccount.value) {
          vm?.$router.push({ name: "home" });
        }
      }
    };
    const onGo = async (path: LoginModalStep, evt?: { e: MouseEvent; navigate: Function }) => {
      if (evt) {
        evt.e.preventDefault();
        evt.e.stopPropagation();
      }
      if (path === LoginModalStep.BACK_FROM_CONFIRM) {
        await vm?.$store.dispatch(ACTION_TYPES.LOGOUT);
        step.value = LoginModalStep.SIGNUP;
      } else if (path === LoginModalStep.FORGOT) {
        vm?.$router.push({ name: "loginForgot" });
        await closeModal();
      } else {
        step.value = path;
      }
    };

    const goToLogin = (evt?: { e: MouseEvent; navigate: Function }): void => {
      isSignUpFlow.value = false;
      onGo(LoginModalStep.LOGIN, evt);
    };
    const goToSignUp = (evt?: { e: MouseEvent; navigate: Function }): void => {
      isSignUpFlow.value = true;
      onGo(LoginModalStep.SIGNUP, evt);
    };

    const onChangeLoginRole = (evt?: { e: MouseEvent; navigate: Function }): void => {
      if (evt) {
        evt.e.preventDefault();
        evt.e.stopPropagation();
      }
      loginRole.value = loginRole.value === ROLES.CREATOR ? ROLES.MEMBER : ROLES.CREATOR;
    };

    watch(step, newStep => {
      if (newStep === LoginModalStep.CODE_CONFIRM) {
        startPeriodicalConfirmationCheck(closeModal);
      } else {
        stopPeriodicalConfirmationCheck();
      }
    });

    emitter.on<LoginSignUpModalData>(EVENTS.SHOW_LOGIN_SIGNUP_MODAL, showModal);
    onBeforeUnmount(() => emitter.off(EVENTS.SHOW_LOGIN_SIGNUP_MODAL, showModal));

    return {
      show,
      step,
      showConfirm,
      isSwitchAccount,
      loginRole,
      closeModal,
      onCloseModal,
      onGo,
      onLogin,
      goToLogin,
      goToSignUp,
      onChangeLoginRole,
      LoginModalStep,
    };
  },
});
